import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Zap, 
  FileSpreadsheet, 
  Server, 
  Shirt, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestCard = ({ title, description, icon: Icon, onTest, result, loading }) => (
  <Card className="relative overflow-hidden">
    <CardHeader>
      <CardTitle className="text-lg font-heading flex items-center gap-2">
        <Icon className="w-5 h-5 text-forest" />
        {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="flex items-center justify-between">
        <Button
          onClick={onTest}
          disabled={loading}
          variant="outline"
          className="flex items-center gap-2"
          data-testid={`test-${title.toLowerCase().replace(/\s/g, '-')}-btn`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          {loading ? 'Testing...' : 'Run Test'}
        </Button>
        
        {result && (
          <div className={`flex items-center gap-2 ${result.success ? 'text-signal-success' : 'text-signal-error'}`}>
            {result.success ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{result.success ? 'Success' : 'Failed'}</span>
          </div>
        )}
      </div>
      
      {result && (
        <div className={`mt-4 p-3 rounded-lg text-sm font-mono ${
          result.success ? 'bg-signal-success/10 text-signal-success' : 'bg-signal-error/10 text-signal-error'
        }`}>
          {result.message}
        </div>
      )}
    </CardContent>
  </Card>
);

export const TestTools = () => {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});

  const runTest = async (testName, endpoint) => {
    setLoading(prev => ({ ...prev, [testName]: true }));
    setResults(prev => ({ ...prev, [testName]: null }));
    
    try {
      const response = await axios.post(`${API}${endpoint}`);
      setResults(prev => ({ ...prev, [testName]: response.data }));
      
      if (response.data.success) {
        toast.success(`${testName} test passed`);
      } else {
        toast.error(`${testName} test failed`);
      }
    } catch (error) {
      const errorResult = { 
        success: false, 
        message: error.response?.data?.detail || error.message 
      };
      setResults(prev => ({ ...prev, [testName]: errorResult }));
      toast.error(`${testName} test failed`);
    } finally {
      setLoading(prev => ({ ...prev, [testName]: false }));
    }
  };

  const triggerSheetsPush = async () => {
    setLoading(prev => ({ ...prev, 'sheets-push': true }));
    try {
      const response = await axios.post(`${API}/sync/sheets-push`);
      toast.success(response.data.message);
      setResults(prev => ({ ...prev, 'sheets-push': response.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sheets push failed");
    } finally {
      setLoading(prev => ({ ...prev, 'sheets-push': false }));
    }
  };

  const triggerTshirtDigest = async () => {
    setLoading(prev => ({ ...prev, 'tshirt-digest': true }));
    try {
      const response = await axios.post(`${API}/sync/tshirt-digest`);
      toast.success(response.data.message);
      setResults(prev => ({ ...prev, 'tshirt-digest': response.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Digest failed");
    } finally {
      setLoading(prev => ({ ...prev, 'tshirt-digest': false }));
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 fade-in max-w-4xl" data-testid="test-tools-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-stone-900 tracking-tight">Test Tools</h1>
        <p className="text-stone-500 mt-1">Test integrations and trigger workflows manually</p>
      </div>

      {/* Connection Tests */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-stone-800 mb-4">Connection Tests</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <TestCard
            title="CampMinder API"
            description="Test authentication and API connectivity"
            icon={Server}
            onTest={() => runTest('CampMinder', '/test/campminder')}
            result={results['CampMinder']}
            loading={loading['CampMinder']}
          />
          
          <TestCard
            title="Google Sheets"
            description="Test connection to configured spreadsheet"
            icon={FileSpreadsheet}
            onTest={() => runTest('Sheets', '/test/sheets')}
            result={results['Sheets']}
            loading={loading['Sheets']}
          />
          
          <TestCard
            title="Parent Email Webhook"
            description="Send test payload to parent email webhook"
            icon={Mail}
            onTest={() => runTest('ParentWebhook', '/test/parent-webhook')}
            result={results['ParentWebhook']}
            loading={loading['ParentWebhook']}
          />
          
          <TestCard
            title="T-Shirt Distribution Webhook"
            description="Send test payload to t-shirt digest webhook"
            icon={Shirt}
            onTest={() => runTest('TshirtWebhook', '/test/tshirt-webhook')}
            result={results['TshirtWebhook']}
            loading={loading['TshirtWebhook']}
          />
        </div>
      </div>

      <Separator />

      {/* Manual Triggers */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-stone-800 mb-4">Manual Triggers</h2>
        <p className="text-stone-500 mb-4">
          These buttons trigger the same workflows that Zapier will call on schedule
        </p>
        
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-signal-info" />
                Google Sheets Push
              </CardTitle>
              <CardDescription>
                Push pending enrollees to Google Sheets (normally runs at 12:05 AM ET)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={triggerSheetsPush}
                disabled={loading['sheets-push']}
                className="bg-signal-info hover:bg-signal-info/90 text-white w-full"
                data-testid="trigger-sheets-push-btn"
              >
                {loading['sheets-push'] ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Trigger Sheets Push
              </Button>
              {results['sheets-push'] && (
                <p className={`mt-3 text-sm ${results['sheets-push'].success ? 'text-signal-success' : 'text-signal-error'}`}>
                  {results['sheets-push'].message}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Shirt className="w-5 h-5 text-earth" />
                T-Shirt Digest
              </CardTitle>
              <CardDescription>
                Send t-shirt distribution digest (normally runs at 9:00 AM ET)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={triggerTshirtDigest}
                disabled={loading['tshirt-digest']}
                className="bg-earth hover:bg-earth-muted text-white w-full"
                data-testid="trigger-tshirt-digest-btn"
              >
                {loading['tshirt-digest'] ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Trigger T-Shirt Digest
              </Button>
              {results['tshirt-digest'] && (
                <p className={`mt-3 text-sm ${results['tshirt-digest'].success ? 'text-signal-success' : 'text-signal-error'}`}>
                  {results['tshirt-digest'].message}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Zapier Endpoint Reference */}
      <Card className="bg-stone-50">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Zapier Webhook Endpoints</CardTitle>
          <CardDescription>
            Configure these endpoints in your Zapier zaps as webhook triggers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 font-mono text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">12:00 AM - Daily Sync</p>
              <code className="block bg-white px-3 py-2 rounded border border-stone-200 text-forest">
                POST {process.env.REACT_APP_BACKEND_URL}/api/sync/zapier-trigger
              </code>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">12:05 AM - Sheets Push</p>
              <code className="block bg-white px-3 py-2 rounded border border-stone-200 text-forest">
                POST {process.env.REACT_APP_BACKEND_URL}/api/sync/sheets-push
              </code>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">9:00 AM - T-Shirt Digest</p>
              <code className="block bg-white px-3 py-2 rounded border border-stone-200 text-forest">
                POST {process.env.REACT_APP_BACKEND_URL}/api/sync/tshirt-digest
              </code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
